import { FormInstance } from 'antd/es/form';
import { SearchOutlined } from '@ant-design/icons';

import { Input as AntInput, Empty, Radio } from 'antd';
import { Form, Select, Input, Tag, Text, Checkbox } from '@bancobarigui/barihaus';

import { useAssignForm } from './useAssignForm';

interface AssignFormProps {
  form: FormInstance;
  isInitialAssignment?: boolean;
}

export function AssignForm({ form, isInitialAssignment = false }: AssignFormProps) {
  const {
    destinationOptions,
    filteredProviders,
    filteredInspectors,
    searchTerm,
    showOnlyQualified,
    selectedDestination,
    isLoadingProviders,
    isLoadingInspectors,
    handleSearchChange,
    handleQualifiedToggle,
    handleDestinationChange,
  } = useAssignForm({ form, isInitialAssignment });

  return (
    <>
      <Form.Item
        name="destination"
        label="Destinatário"
        rules={[{ required: true, message: 'Selecione o destinatário' }]}
      >
        <Select
          placeholder="Destinatário"
          options={destinationOptions}
          onChange={handleDestinationChange}
          disabled={isInitialAssignment}
        />
      </Form.Item>

      {selectedDestination === 'provider' && (
        <div className="flex gap-3 mb-4">
          <Tag className="bg-grey_100">Prestadora</Tag>
        </div>
      )}

      {selectedDestination === 'inspector' && (
        <div className="flex gap-3 mb-4">
          <Tag className="bg-blue_100">Inspetor (Direcionamento Direto)</Tag>
        </div>
      )}

      <Form.Item name="notes" label="Nota de Direcionamento" rules={[{ required: false }]}>
        <AntInput.TextArea placeholder="Adicionar Nota" rows={4} maxLength={500} />
      </Form.Item>

      {selectedDestination === 'provider' && (
        <>
          <div className="mb-4">
            <Text strong className="block mb-2">
              PRESTADORA
            </Text>
            <Form.Item name="searchProvider" noStyle>
              <Input
                placeholder="Pesquise por nome, CPF ou e-mail"
                prefix={<SearchOutlined className="text-grey_500" />}
                value={searchTerm}
                onChange={handleSearchChange}
                className="mb-4"
              />
            </Form.Item>
          </div>

          <Form.Item name="showOnlyQualifiedProvider" valuePropName="checked" noStyle>
            <div className="flex items-center justify-between mb-4">
              <Text>Somente prestadoras aptas:</Text>
              <Checkbox checked={showOnlyQualified} onChange={handleQualifiedToggle} />
            </div>
          </Form.Item>

          <Form.Item
            name="providerId"
            rules={[{ required: true, message: 'Selecione uma prestadora' }]}
          >
            <Radio.Group className="w-full">
              <div className="flex flex-col border-2 border-dashed border-grey_200 rounded-lg p-6 min-h-[200px]">
                {isLoadingProviders ? (
                  <div className="flex items-center justify-center flex-1">
                    <Text className="text-grey_500">Carregando prestadoras...</Text>
                  </div>
                ) : filteredProviders.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {filteredProviders.map((provider) => (
                      <div
                        key={provider.id}
                        className="flex items-center gap-3 p-3 border border-grey_200 rounded-lg hover:bg-grey_50 cursor-pointer"
                      >
                        <Radio value={provider.id} />
                        <div className="flex-1">
                          <Text strong>{provider.name}</Text>
                          <Text className="block text-sm text-grey_500">{provider.cnpj}</Text>
                        </div>
                        {provider.allowsDemandReceiving && (
                          <Tag color="green" showBorder>
                            Apta
                          </Tag>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center flex-1">
                    <Empty
                      description={<Text className="text-grey_500">Nenhum registro encontrado.</Text>}
                    />
                  </div>
                )}
              </div>
            </Radio.Group>
          </Form.Item>
        </>
      )}

      {selectedDestination === 'inspector' && (
        <>
          <div className="mb-4">
            <Text strong className="block mb-2">
              INSPETOR
            </Text>
            <Form.Item name="searchInspector" noStyle>
              <Input
                placeholder="Pesquise por nome ou CPF"
                prefix={<SearchOutlined className="text-grey_500" />}
                value={searchTerm}
                onChange={handleSearchChange}
                className="mb-4"
              />
            </Form.Item>
          </div>

          <Form.Item name="showOnlyQualifiedInspector" valuePropName="checked" noStyle>
            <div className="flex items-center justify-between mb-4">
              <Text>Somente inspetores aptos:</Text>
              <Checkbox checked={showOnlyQualified} onChange={handleQualifiedToggle} />
            </div>
          </Form.Item>

          <Form.Item
            name="inspectorId"
            rules={[{ required: true, message: 'Selecione um inspetor' }]}
          >
            <Radio.Group className="w-full">
              <div className="flex flex-col border-2 border-dashed border-grey_200 rounded-lg p-6 min-h-[200px]">
                {isLoadingInspectors ? (
                  <div className="flex items-center justify-center flex-1">
                    <Text className="text-grey_500">Carregando inspetores...</Text>
                  </div>
                ) : filteredInspectors.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {filteredInspectors.map((inspector) => (
                      <div
                        key={inspector.id}
                        className="flex items-center gap-3 p-3 border border-grey_200 rounded-lg hover:bg-grey_50 cursor-pointer"
                      >
                        <Radio value={inspector.userId} />
                        <div className="flex-1">
                          <Text strong>{inspector.name}</Text>
                          <Text className="block text-sm text-grey_500">{inspector.cpf}</Text>
                        </div>
                        {inspector.allowsDemandReceiving && (
                          <Tag color="green" showBorder>
                            Apto
                          </Tag>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center flex-1">
                    <Empty
                      description={<Text className="text-grey_500">Nenhum inspetor com direcionamento direto habilitado.</Text>}
                    />
                  </div>
                )}
              </div>
            </Radio.Group>
          </Form.Item>
        </>
      )}
    </>
  );
}
